import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, Expand, Download, Camera } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { GALLERY, ORG } from "../data/content";

const ease = [0.22, 1, 0.36, 1];

async function downloadPhoto(item) {
  try {
    const res = await fetch(item.src);
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${item.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Photo downloading…", { description: item.title });
  } catch {
    window.open(item.src, "_blank", "noopener,noreferrer");
  }
}

export default function Gallery() {
  const [active, setActive] = useState(null);
  const items = GALLERY;

  const next = () => setActive((a) => (a === null ? null : (a + 1) % items.length));
  const prev = () => setActive((a) => (a === null ? null : (a - 1 + items.length) % items.length));

  useEffect(() => {
    if (active === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.documentElement.style.overflow = "hidden";
    window.lenis?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.documentElement.style.overflow = "";
      window.lenis?.start();
    };
  }, [active, items.length]);

  return (
    <section id="gallery" className="bg-sanddark py-16 sm:py-24 lg:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <SectionHeading
          align="center"
          eyebrow="Gallery"
          title="Moments from"
          accent="the ground."
          sub="Real photos from our Punjab floods relief — more coming as we grow. Tap any photo to view or download it."
        />

        {/* Grid */}
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
          {items.map((item, i) => (
            <motion.button
              key={item.src}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: 0.08 * i, ease }}
              onClick={() => setActive(i)}
              data-testid={`gallery-item-${i}`}
              aria-label={`Open photo: ${item.title}`}
              className="group relative block w-full overflow-hidden rounded-3xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#D4AF37]"
            >
              <img
                src={item.src}
                alt={item.title}
                loading="lazy"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-[#08353D]/85 via-transparent to-transparent p-5 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <span className="translate-y-3 text-left font-display text-lg font-bold text-white transition-transform duration-300 group-hover:translate-y-0">
                  {item.title}
                </span>
                <span className="translate-y-3 text-left text-xs text-[#F3E096] transition-transform delay-75 duration-300 group-hover:translate-y-0">
                  Punjab floods
                </span>
              </span>
              <span className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#0D4B56] opacity-0 transition-all duration-300 group-hover:opacity-100">
                <Expand size={16} />
              </span>
            </motion.button>
          ))}

          {/* Coming soon teaser */}
          <motion.a
            href={ORG.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5, delay: 0.3, ease }}
            data-testid="gallery-exhibition-teaser"
            className="group relative flex aspect-[4/5] w-full flex-col items-center justify-center gap-4 overflow-hidden rounded-3xl bg-gradient-to-br from-[#0D4B56] to-[#08353D] p-8 text-center"
          >
            <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#D4AF37]/15 blur-2xl" />
            <span className="flex h-16 w-16 items-center justify-center rounded-3xl bg-white/10 text-[#F3E096] transition-transform duration-500 group-hover:scale-110">
              <Camera size={32} />
            </span>
            <span className="font-script text-3xl font-bold text-[#F3E096]">Exhibition photos</span>
            <span className="text-sm font-semibold text-white/70">coming soon — follow us on Instagram</span>
          </motion.a>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && items[active] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-[#08353D]/95 p-4 backdrop-blur-md sm:p-10"
            onClick={() => setActive(null)}
            data-testid="lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`Photo: ${items[active].title}`}
          >
            <button
              onClick={() => setActive(null)}
              aria-label="Close lightbox"
              data-testid="lightbox-close"
              className="absolute right-5 top-5 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-[#D4AF37] hover:text-[#08353D]"
            >
              <X size={22} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              aria-label="Previous photo"
              data-testid="lightbox-prev"
              className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-[#D4AF37] hover:text-[#08353D] sm:left-8"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              aria-label="Next photo"
              data-testid="lightbox-next"
              className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-[#D4AF37] hover:text-[#08353D] sm:right-8"
            >
              <ChevronRight size={24} />
            </button>
            <motion.figure
              key={items[active].src}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease }}
              className="max-h-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={items[active].src}
                alt={items[active].title}
                className="max-h-[76vh] w-auto rounded-2xl object-contain shadow-2xl"
              />
              <figcaption className="mt-4 text-center">
                <p className="font-display text-lg font-bold text-white">{items[active].title}</p>
                <p className="mt-1 text-sm text-[#F3E096]">{items[active].caption}</p>
                <p className="mt-1 text-xs text-white/50">
                  {active + 1} / {items.length}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadPhoto(items[active]);
                  }}
                  data-testid="lightbox-download"
                  aria-label={`Download photo: ${items[active].title}`}
                  className="mt-4 inline-flex items-center gap-2 rounded-full gold-shimmer px-6 py-2.5 text-sm font-bold text-[#08353D] transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <Download size={16} /> Download Photo
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
